<!--@format-->
<template>
    <div
        class="gst-hotel-selected-card d-flex flex-row px-2 py-4 pa-md-0 mx-md-2 mt-md-2"
        :class="{ 'gst-hotel-selected-card--promoted': isPromotedCard }"
        tabindex="0"
        @click="openItemDetails()"
        @keypress.enter="openItemDetails()">
        <PromotedCardChip
            v-if="item.campaign.type"
            class="promoted-card-chip"
            :background-color="item.campaign.config.backgroundColor"
            :font-color="item.campaign.config.fontColor"
            :icon-url="item.campaign.config.iconUrl" />
        <div
            v-if="$vuetify.breakpoint.mdAndUp"
            class="align-self-stretch u-position-relative"
            :style="{ width: $vuetify.breakpoint.mdAndUp ? '100px' : '80px' }">
            <BaseImage
                :src="image"
                gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
                class="u-position-absolute"
                height="100%"
                width="100%" />
        </div>
        <div class="py-md-4 flex-grow-1">
            <div class="d-flex flex-row">
                <div
                    v-if="!$vuetify.breakpoint.mdAndUp"
                    class="flex-shrink-1">
                    <BaseImage
                        :src="image"
                        gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
                        height="56"
                        width="100" />
                </div>
                <div class="d-flex flex-column justify-center pl-2 px-md-2">
                    <div>
                        <StarRating
                            :value="stars"
                            class="gst-hotel-selected-card__rating" />
                        <VClamp
                            v-if="$vuetify.breakpoint.mdAndUp"
                            autoresize
                            :max-lines="2"
                            :title="distance"
                            tag="span"
                            class="gst-hotel-selected-card__distance pl-1">
                            • {{ distance }}
                        </VClamp>
                    </div>
                    <div>
                        <VClamp
                            autoresize
                            :max-lines="1"
                            :title="item.name"
                            tag="h6"
                            class="gst-hotel-selected-card__name">
                            {{ item.name }}
                        </VClamp>
                    </div>
                    <VClamp
                        v-if="!$vuetify.breakpoint.mdAndUp"
                        autoresize
                        :max-lines="2"
                        :title="distance"
                        tag="span"
                        class="gst-hotel-selected-card__distance">
                        {{ distance }}
                    </VClamp>
                </div>
            </div>
            <div class="d-flex flex-row mt-2 px-md-2">
                <div class="flex-grow-1">
                    <span class="gst-hotel-selected-card__room-name d-inline-block">
                        {{ room | roomName }}
                    </span>
                    <div
                        v-if="showGiftCard || showDiscountAmount"
                        class="gst-hotel-selected-card__gifts d-flex flex-wrap my-1">
                        <SaveAmountCardChip
                            v-if="showDiscountAmount"
                            :amount="bundleProductsTotals.discountedAmount"
                            :currency="currency" />
                        <GiftCardChip
                            v-if="showGiftCard"
                            :amount="giftCard.amount"
                            :currency="giftCard.currency" />
                    </div>
                    <PriceDetail
                        show-tax-on-newline
                        :all-inclusive-pricing="allInclusivePricing"
                        :total-prices="bundleProductsTotals"
                        :currency="currency"
                        :price-per-night="pricePerNight"
                        :points="redeemPoints"
                        :points-per-night="pointsPerNight" />
                    <div class="d-flex justify-space-between">
                        <div>
                            <VClamp
                                autoresize
                                :max-lines="1"
                                :title="bundleProducts | bundleProductsQuantity"
                                class="gst-hotel-selected-card__bundle-products pb-1">
                                {{ bundleProducts | bundleProductsQuantity }}
                            </VClamp>
                            <LoyaltyInfoChip
                                :show-min-max="!userIsAuth"
                                :points-info="pointsInfo"
                                :lowest-tier-status="
                                    $t(
                                        'shared:components.bonusPoints.loyaltyInfoChip.regularMember'
                                    )
                                "
                                :highest-tier-status="
                                    $t(
                                        'shared:components.bonusPoints.loyaltyInfoChip.memberWithStatus'
                                    )
                                " />
                        </div>
                        <BaseButton
                            v-if="$vuetify.breakpoint.mdAndUp"
                            :placeholder="$t('_common:buttons.remove')"
                            class="gst-hotel-selected-card__btn mt-md-2"
                            color="primary"
                            @click="removeItem" />
                    </div>
                </div>
                <div
                    v-if="!$vuetify.breakpoint.mdAndUp"
                    class="d-flex align-center">
                    <BaseButton
                        :placeholder="$t('_common:buttons.remove')"
                        class="gst-hotel-selected-card__btn"
                        color="primary"
                        @click="removeItem" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VClamp from 'vue-clamp';
import StarRating from '@core/shared/components/misc/StarRating.vue';
import locationsConstants from '@tenant/app/utils/constants/locations';
import { create as CreateBundleProductsModel } from '@tenant/app/modelsV2/BundleProductsModel';
import { getImageByWidth } from '@tenant/app/utils/imageModelUtils';
import { IMAGE_SIZE } from '@tenant/app/utils/constants/imageData';
import GiftCardChip from '@tenant/app/components/chips/GiftCardChip';
import SaveAmountCardChip from '@tenant/app/components/chips/SaveAmountCardChip';
import PriceDetail from '@tenant/app/modules/hotel/components/PriceDetail.vue';
import PromotedCardChip from '@tenant/app/components/chips/PromotedCardChip.vue';
import EventFlowMixin from '@tenant/app/mixins/EventFlowMixin.js';
import LoyaltyInfoChip from '@tenant/app/components/chips/LoyaltyInfoChip.vue';

export default {
    name: 'HotelSelectedCard',
    components: {
        VClamp,
        StarRating,
        GiftCardChip,
        SaveAmountCardChip,
        PriceDetail,
        PromotedCardChip,
        LoyaltyInfoChip,
    },
    mixins: [EventFlowMixin],
    props: {
        item: {
            type: Object,
            required: true,
        },
        distanceUnitOfMeasure: {
            type: String,
            required: true,
        },
        distanceFrom: {
            type: Boolean,
            default: false,
        },
        inLocation: {
            type: Object,
            required: true,
        },
        bundleProducts: {
            type: Object,
            default: function () {
                return CreateBundleProductsModel();
            },
        },
        busEventsParent: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapGetters({
            redeemPointsToggle: 'appState/getRedeemPointsToggle',
            userIsAuth: 'user/isAuth',
        }),
        stars() {
            const { awards } = this.item;

            if (awards.length && awards[0].rating) {
                return awards[0].rating;
            }

            return 0;
        },
        image() {
            const size = this.$vuetify.breakpoint.mdAndUp ? IMAGE_SIZE.LARGE : IMAGE_SIZE.THUMBNAIL;
            return getImageByWidth(size, this.item.image);
        },
        distance() {
            if (this.distanceFrom) {
                return this.$options.filters.distanceFrom(
                    this.item.distance,
                    this.distanceUnitOfMeasure,
                    !this.isManageBookingFlow
                        ? this.$t(
                              this.inLocation.type === locationsConstants.TYPES.CITY
                                  ? '_common:terms.cityCenter'
                                  : '_common:terms.venue'
                          )
                        : this.$t('_common:terms.originalHotel')
                );
            }
            return this.$options.filters.distanceToVenue(
                this.item.distance,
                this.distanceUnitOfMeasure
            );
        },
        room() {
            return this.item.selectedRoom;
        },
        rooms() {
            return this.item.rooms;
        },
        roomTotalBasePrice() {
            return this.room.selectedRate.totals.basePrice;
        },
        bundleProductsTotals() {
            return this.bundleProducts.getTotals();
        },
        showBasePriceWithoutDiscount() {
            return (
                this.bundleProductsTotals.basePrice !==
                this.bundleProductsTotals.basePriceWithoutDiscount
            );
        },
        pricePerNight() {
            return this.room.selectedRate.pricePerNight;
        },
        giftCard() {
            return this.room.selectedRate.giftCard;
        },
        showGiftCard() {
            return this.giftCard ? this.giftCard.amount : 0;
        },
        showDiscountAmount() {
            return this.showBasePriceWithoutDiscount && this.bundleProductsTotals.discountedAmount;
        },
        currency() {
            return this.room.selectedRate.currency;
        },
        isPromotedCard() {
            return this.item.campaign?.type;
        },
        allInclusivePricing() {
            return this.item.allInclusivePricing;
        },
        pointsEarned() {
            return this.item.selectedRoom.selectedRate.pointsEarned;
        },
        redeemPoints() {
            return this.item.selectedRoom.selectedRate.redeemPoints;
        },
        pointsPerNight() {
            return this.item.selectedRoom.selectedRate.redeemPointsPerNight;
        },
        pointsInfo() {
            return {
                minimum: this.redeemPointsToggle
                    ? this.item.selectedRoom.selectedRate.redeemPointsRange.minimum
                    : this.item.selectedRoom.selectedRate.earnPointsRange.minimum,
                maximum: this.redeemPointsToggle
                    ? this.item.selectedRoom.selectedRate.redeemPointsRange.maximum
                    : this.item.selectedRoom.selectedRate.earnPointsRange.maximum,
                actual: this.redeemPointsToggle ? this.redeemPoints : this.pointsEarned,
            };
        },
    },
    methods: {
        openItemDetails(focusedRoom) {
            this.busEventsParent.$emit('open-item-details', this.item, focusedRoom);
        },
        removeItem(event) {
            event.stopPropagation();
            this.busEventsParent.$emit('remove-item', this.item);
        },
        onRoomDropDownInputDo(value) {
            this.busEventsParent.$emit('change-room', { id: this.item.id, value });
        },
    },
};
</script>

<style lang="scss">
@import '@scssVariables';
@import '@scssMixins';

.gst-hotel-selected-card {
    @include font-size('m');
    @include font-weight('regular');

    border: 1px solid theme-color-hex('primary');
    background-color: #ebf5ff;
    border-radius: 3px;
    overflow: hidden;

    .v-image {
        border-radius: 2px;
    }

    .gst-hotel-selected-card__rating {
        height: font-size('xxs');
        margin-top: 2px;
        float: left;
    }

    .gst-hotel-selected-card__distance {
        @include font-size('xxs');
        @include line-height('m');

        color: theme-color('tertiary');
    }

    .gst-hotel-selected-card__name {
        @include font-size('s');
        @include font-weight('large');
        @include line-height('xxxl');

        color: theme-color('quaternary');
    }

    .gst-hotel-selected-card__room {
        @include font-size('s');
        @include line-height('xxxl');

        position: relative;
        top: theme-spacing(1);
    }

    .gst-hotel-selected-card__room-name {
        color: theme-color('tertiary');
        font-size: font-size('xxs');
        font-weight: font-weight('regular');
    }

    .gst-hotel-selected-card__bundle-products {
        @include font-size('xxs');
        @include line-height('m');

        margin: theme-spacing(1) 0;
        color: theme-color('tertiary');
    }

    .gst-hotel-selected-card__gifts {
        gap: theme-spacing(1);
    }

    .gst-hotel-selected-card__btn {
        border: 1px solid theme-color('primary');
        background-color: theme-color('white') !important;
        color: theme-color('primary') !important;
        font-weight: font-weight('large');
        letter-spacing: 0;
        text-transform: capitalize;
    }

    .gst-hotel-selected-card__icon {
        path {
            fill: theme-color('tertiary');
        }
    }

    .gst-hotel-selected-card__icon:hover {
        path {
            fill: theme-color('primary');
        }
    }

    &.gst-hotel-selected-card--promoted {
        border: 1px solid theme-color('default-promoted-bg');
    }
}

.promoted-card-chip {
    margin-right: theme-spacing(2);
}

@include mobile-only {
    .gst-hotel-selected-card {
        border: theme-color('transparent') !important;
        background: theme-color('transparent') !important;
        border-bottom: 1px solid theme-color-hex('senary-darken-2');
        border-radius: border-radius('none');

        &:hover {
            border: theme-color('transparent');
            background-color: #ebf5ff;
            border-bottom: 1px solid #ebf5ff;
        }

        .gst-hotel-selected-card__distance {
            margin-top: theme-spacing(1);
        }

        .gst-hotel-selected-card__name {
            position: relative;
            top: 2px;
        }

        .gst-hotel-selected-card__rating {
            position: relative;
            bottom: theme-spacing(1);
        }
    }
}
</style>
